import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth, hasRole } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { GraduationCap, CheckCircle2, XCircle, Ban } from "lucide-react";

const SCOPE_LABEL = {
  articolo_listino: "Articolo listino",
  cliente: "Cliente",
  settore: "Settore",
};

const STATUS_VARIANT = {
  active: "default",
  pending_approval: "secondary",
  rejected: "outline",
};

const STATUS_LABEL = {
  active: "Attiva",
  pending_approval: "In attesa",
  rejected: "Rifiutata",
};

export default function AdminLearningRules() {
  const { user } = useAuth();
  const canApprove = hasRole(user, "titolare", "super_admin");
  const [rules, setRules] = useState([]);
  const [filterScope, setFilterScope] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const highlightId = location.hash ? location.hash.replace("#", "") : null;

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterScope !== "all") params.scope = filterScope;
      if (filterStatus !== "all") params.status = filterStatus;
      const qs = new URLSearchParams(params).toString();
      const data = await api.get(`/learning-row-rules${qs ? `?${qs}` : ""}`);
      setRules(data.items || []);
    } catch (e) {
      toast.error(`Errore caricamento regole: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [filterScope, filterStatus]);

  const filtered = rules.filter((r) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      r.scope_value?.toLowerCase().includes(s) ||
      r.motivazione?.toLowerCase().includes(s) ||
      r.created_by?.name?.toLowerCase().includes(s)
    );
  });

  const doApprove = async (id) => {
    try {
      await api.post(`/learning-row-rules/${id}/approve`);
      toast.success("Regola approvata");
      load();
    } catch (e) {
      toast.error(`Errore: ${e.message}`);
    }
  };
  const doReject = async (id) => {
    try {
      await api.post(`/learning-row-rules/${id}/reject`);
      toast.success("Regola rifiutata");
      load();
    } catch (e) {
      toast.error(`Errore: ${e.message}`);
    }
  };
  const doDisable = async (id) => {
    if (!confirm("Disattivare la regola? L'azione non è reversibile.")) return;
    try {
      await api.del(`/learning-row-rules/${id}`);
      toast.success("Regola disattivata");
      load();
    } catch (e) {
      toast.error(`Errore: ${e.message}`);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-learning-rules">
      <div className="flex items-center gap-3 mb-6">
        <GraduationCap className="h-6 w-6 text-sky-600" />
        <h1 className="text-2xl font-semibold">Regole apprese</h1>
        <Badge variant="outline" className="ml-auto">
          {filtered.length} regole
        </Badge>
      </div>

      <Card className="mb-4">
        <CardContent className="pt-4 flex flex-wrap gap-3 items-end">
          <div>
            <label className="text-xs text-slate-500">Scope</label>
            <Select value={filterScope} onValueChange={setFilterScope}>
              <SelectTrigger className="w-48" data-testid="filter-scope">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="articolo_listino">Articolo listino</SelectItem>
                <SelectItem value="cliente">Cliente</SelectItem>
                <SelectItem value="settore">Settore</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Status</label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-48" data-testid="filter-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="active">Attive</SelectItem>
                <SelectItem value="pending_approval">In attesa</SelectItem>
                <SelectItem value="rejected">Rifiutate</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs text-slate-500">Cerca</label>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="motivazione / scope / autore…"
              data-testid="filter-search"
            />
          </div>
          <Button variant="outline" onClick={load} disabled={loading}>
            {loading ? "Carico…" : "Aggiorna"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Tutte le regole</CardTitle>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center" data-testid="empty-state">
              Nessuna regola trovata. Crea la prima dal Workbench:
              kebab voce → "Insegna su questa voce".
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-xs text-slate-500 text-left">
                    <th className="py-2 px-2">Status</th>
                    <th className="py-2 px-2">Scope</th>
                    <th className="py-2 px-2">Motivazione</th>
                    <th className="py-2 px-2">Override</th>
                    <th className="py-2 px-2">Autore</th>
                    <th className="py-2 px-2 text-right">Applicata</th>
                    <th className="py-2 px-2 text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr
                      key={r.id}
                      className={`border-b hover:bg-slate-50 ${
                        r.id === highlightId ? "bg-sky-50" : ""
                      }`}
                      data-testid={`rule-row-${r.id}`}
                    >
                      <td className="py-2 px-2">
                        <Badge variant={STATUS_VARIANT[r.status] || "outline"}>
                          {STATUS_LABEL[r.status] || r.status}
                        </Badge>
                      </td>
                      <td className="py-2 px-2">
                        <div className="text-xs text-slate-500">
                          {SCOPE_LABEL[r.scope] || r.scope}
                        </div>
                        <div className="font-mono text-xs">{r.scope_value}</div>
                      </td>
                      <td className="py-2 px-2 max-w-md">
                        <div className="text-xs italic">"{r.motivazione}"</div>
                      </td>
                      <td className="py-2 px-2">
                        <div className="text-xs space-y-0.5">
                          {Object.entries(r.output_overrides || {}).map(([k, v]) => (
                            <div key={k}>
                              <span className="font-mono text-slate-500">{k}</span>{" "}
                              <span>→ {String(v)}</span>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="py-2 px-2 text-xs">
                        <div>{r.created_by?.name || "—"}</div>
                        <div className="text-slate-400">
                          {r.created_at
                            ? new Date(r.created_at).toLocaleDateString("it-IT")
                            : ""}
                        </div>
                      </td>
                      <td className="py-2 px-2 text-right text-xs">
                        {r.times_applied || 0}×
                      </td>
                      <td className="py-2 px-2 text-right">
                        {canApprove && r.status === "pending_approval" && (
                          <div className="flex gap-1 justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => doApprove(r.id)}
                              data-testid={`approve-${r.id}`}
                            >
                              <CheckCircle2 className="h-3 w-3 mr-1" /> Approva
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => doReject(r.id)}
                              data-testid={`reject-${r.id}`}
                            >
                              <XCircle className="h-3 w-3 mr-1" /> Rifiuta
                            </Button>
                          </div>
                        )}
                        {canApprove && r.status === "active" && !r.disabled && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => doDisable(r.id)}
                            data-testid={`disable-${r.id}`}
                          >
                            <Ban className="h-3 w-3 mr-1" /> Disattiva
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
